import { z } from 'zod';

import { HumanTaskBlockConfigSchema, HumanTaskPresentationMode, HumanTaskType } from './config.js';
import { SubmitPriorAuthorizationParsedConfigSchema } from './submitPriorAuthorization.js';
import { SortOrder } from '../../common.js';
import {
  PatientOrderMixedDetermination,
  TerminalAuthorizationStatus,
} from '../../patient/order.js';
import { oneOrMany } from '../../utils.js';

export { HumanTaskType, HumanTaskPresentationMode };

export * from './submitPriorAuthorization.js';

export enum HumanTaskState {
  Pending = 'Pending',
  Claimed = 'Claimed',
  Expired = 'Expired',
  Complete = 'Complete',
}

export const HumanTaskAssetPayloadSchema = z.object({
  name: z.string(),
  contentType: z.string(),
  content: z.string(),
});
export type HumanTaskAssetPayload = z.infer<typeof HumanTaskAssetPayloadSchema>;

export const HumanTaskAssetResponseSchema = z.object({
  success: z.boolean(),
  fileReference: z.string(),
});
export type HumanTaskAssetResponse = z.infer<typeof HumanTaskAssetResponseSchema>;

export const HumanTaskSubtaskStepPayloadSchema = z.object({
  stepValues: z.record(z.string().or(z.array(z.string()))),
});
export type HumanTaskSubtaskStepPayload = z.infer<typeof HumanTaskSubtaskStepPayloadSchema>;

export const HumanTaskPayloadSchema = z.object({
  subtaskValues: z.array(z.record(z.string().or(z.array(z.string())))),
  metadata: z.object({
    userId: z.string().uuid().optional(),
    startedAt: z.string().optional(),
    completedAt: z.string().optional(),
    subtasks: z.array(
      z.object({
        title: z.string(),
        steps: z
          .array(
            z
              .object({
                title: z.string(),
                completedAt: z.string(),
              })
              .nullable()
          )
          .optional(),
        procedureId: z.string().uuid().optional(),
      })
    ),
    updatedSubmissions: z.array(z.record(z.unknown())).optional(),
    authorizationStatus: z
      .nativeEnum(TerminalAuthorizationStatus)
      .or(z.literal(PatientOrderMixedDetermination.MixedDetermination).optional()),
  }),
});
export type HumanTaskPayload = z.infer<typeof HumanTaskPayloadSchema>;

export const HumanTaskParsedConfigSchema = z.discriminatedUnion('taskType', [
  SubmitPriorAuthorizationParsedConfigSchema,
]);
export type HumanTaskParsedConfig = z.infer<typeof HumanTaskParsedConfigSchema>;

export const HumanTaskSchema = z.object({
  id: z.string().uuid(),
  created: z.string().datetime(),
  updated: z.string().datetime().optional(),
  flowRunId: z.string().uuid(),
  blockId: z.string().uuid(),
  name: z.string(),
  config: HumanTaskBlockConfigSchema,
  organizationId: z.string().uuid(),
  state: z.nativeEnum(HumanTaskState),
  assignedToId: z.string().uuid().nullish(),
  expiresAt: z.string().datetime(),
  payload: HumanTaskPayloadSchema.nullish(),
  parsedConfig: HumanTaskParsedConfigSchema.nullish(),
  categoryId: z.string().uuid().nullable(),
  externalId: z.string().uuid().nullish(),
  entityEhrInformationId: z.string().uuid().nullable(),
  entityEncounterId: z.string().uuid().nullable(),
  entityExternalDocumentPacketId: z.string().uuid().nullable(),
  entityPatientOrderId: z.string().uuid().nullable(),
});
export type HumanTask = z.infer<typeof HumanTaskSchema>;

export const HumanTaskSearchResultSchema = HumanTaskSchema.pick({
  // Omit payload and parsedConfig to save bandwidth / memory. This
  // intentionally uses pick instead of omit to match the select query.
  id: true,
  created: true,
  updated: true,
  flowRunId: true,
  blockId: true,
  name: true,
  config: true,
  organizationId: true,
  state: true,
  assignedToId: true,
  expiresAt: true,
  categoryId: true,
  externalId: true,
  entityEhrInformationId: true,
  entityEncounterId: true,
  entityExternalDocumentPacketId: true,
  entityPatientOrderId: true,
}).extend({
  flowId: z.string().uuid(),
});
export type HumanTaskSearchResult = z.infer<typeof HumanTaskSearchResultSchema>;

export const HumanTaskFindAllQuerySchema = z
  .object({
    agentId: z.string().uuid(),
    flowRunId: z.string().uuid(),
    flowId: oneOrMany(z.string().uuid()),
    entityId: oneOrMany(z.string().uuid()),
    organizationId: oneOrMany(z.string().uuid()),
    state: z.nativeEnum(HumanTaskState),
    categoryId: oneOrMany(z.string().uuid()),
    assignedToId: oneOrMany(z.string().uuid()),
    q: z.string(),
    limit: z.coerce.number().int().positive(),
    offset: z.coerce.number().int().nonnegative(),
    sortModel: z.array(
      z.object({
        field: z.string(),
        sort: z.nativeEnum(SortOrder),
      })
    ),
  })
  .partial();
export type HumanTaskFindAllQuery = z.infer<typeof HumanTaskFindAllQuerySchema>;

export const HumanTaskClaimNextQuerySchema = HumanTaskFindAllQuerySchema.omit({
  state: true,
  limit: true,
  offset: true,
});
export type HumanTaskClaimNextQuery = z.infer<typeof HumanTaskClaimNextQuerySchema>;
